import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { getAccountLinks, getUnlinkedAccounts, updateAccountLink } from '../../../../redux/Slices/authSlice';
import { toast } from 'react-toastify';
import { motion } from 'framer-motion';
import { FaLink, FaCheckCircle } from 'react-icons/fa';
import { useTitle } from '../../../../shared/hooks/TitleContext';
import { useTranslation } from 'react-i18next';
import i18n from '../../../../i18n/i18n';
import ReactDOM from 'react-dom';

const AccountLinks = () => {
  const dispatch = useDispatch();
  const { setTitle } = useTitle();
  const { t } = useTranslation('global');
  const isRTL = i18n.language === 'ar';
  const dir = isRTL ? 'rtl' : 'ltr';
  const textAlign = isRTL ? 'right' : 'left';
  const { accountLinks, unlinkedAccounts, isLoading } = useSelector((state) => state.auth);
  const [formData, setFormData] = useState({});
  const [searchQueries, setSearchQueries] = useState({});
  const [filteredAccounts, setFilteredAccounts] = useState({});
  const [openDropdowns, setOpenDropdowns] = useState({});
  const [dropdownPositions, setDropdownPositions] = useState({});
  const inputRefs = useRef({});

  useEffect(() => {
    setTitle('sidenav.accountLinks');
  }, [setTitle]);

  useEffect(() => {
    dispatch(getAccountLinks());
    dispatch(getUnlinkedAccounts());
  }, [dispatch]);

  const getDisabledAccountIds = React.useCallback((currentKey) => {
    const disabledIds = [];
    
    // الحسابات المختارة في formData
    Object.keys(formData).forEach(key => {
      if (key !== currentKey && formData[key]) {
        disabledIds.push(Number(formData[key]));
      }
    });
    
    // الحسابات المربوطة أصلاً (غير المختارة في formData)
    if (accountLinks?.data) {
      accountLinks.data.forEach(link => {
        if (link.key !== currentKey && link.linked_account && !formData[link.key]) {
          disabledIds.push(link.linked_account.id);
        }
      });
    }
    
    return disabledIds;
  }, [formData, accountLinks]);

  // لا نقوم بتهيئة formData تلقائياً - فقط الحسابات التي يغيرها المستخدم

  useEffect(() => {
    if (unlinkedAccounts?.data && accountLinks?.data) {
      const filtered = {};
      accountLinks.data.forEach(link => {
        const searchQuery = searchQueries[link.key] || '';
        let accounts = [...unlinkedAccounts.data]; // نسخة من المصفوفة
        
        // إذا كان هناك بحث، نفلتر الحسابات
        if (searchQuery && searchQuery.trim() !== '') {
          const searchTerm = searchQuery.toLowerCase();
          accounts = accounts.filter(account => 
            account.name_ar?.toLowerCase().includes(searchTerm) ||
            account.name_en?.toLowerCase().includes(searchTerm) ||
            account.code?.toLowerCase().includes(searchTerm)
          );
        }
        
        // عرض جميع الحسابات (سنتعامل مع disabled في العرض)
        filtered[link.key] = accounts;
      });
      setFilteredAccounts(filtered);
    } else if (unlinkedAccounts?.data) {
      const filtered = {};
      accountLinks?.data?.forEach(link => {
        // عرض جميع الحسابات
        filtered[link.key] = [...unlinkedAccounts.data];
      });
      setFilteredAccounts(filtered);
    }
  }, [unlinkedAccounts, accountLinks, searchQueries]);

  const handleSearch = (key, value) => {
    setSearchQueries(prev => ({ ...prev, [key]: value }));
  };

  const handleAccountSelect = (key, accountId) => {
    // البحث عن الحساب المختار من جميع الحسابات المتاحة
    let accountToSelect = null;
    
    // البحث في unlinkedAccounts
    const allAccounts = unlinkedAccounts?.data || [];
    accountToSelect = allAccounts.find(acc => acc.id === accountId);
    
    // إذا لم نجده، نبحث في الحسابات المرتبطة
    if (!accountToSelect) {
      const link = accountLinks?.data?.find(link => link.key === key);
      if (link?.linked_account && link.linked_account.id === accountId) {
        accountToSelect = link.linked_account;
      }
    }
    
    // تحديث formData
    setFormData(prev => ({ ...prev, [key]: accountId.toString() }));
    
    // تصفير البحث بعد الاختيار حتى لا يظل الفلتر فعالاً في المرة التالية
    setSearchQueries(prev => ({ ...prev, [key]: '' }));
    
    setOpenDropdowns(prev => ({ ...prev, [key]: false }));
  };

  const calculateDropdownPosition = useCallback((key) => {
    const inputElement = inputRefs.current[key];
    if (!inputElement) return null;

    const rect = inputElement.getBoundingClientRect();
    const viewportHeight = window.innerHeight;
    const dropdownMaxHeight = 300;
    const spaceBelow = viewportHeight - rect.bottom;
    const spaceAbove = rect.top;
    
    // Calculate optimal position
    let top = rect.bottom + 5;
    let maxHeight = dropdownMaxHeight;
    
    // If not enough space below, show above
    if (spaceBelow < dropdownMaxHeight && spaceAbove > spaceBelow) {
      top = rect.top - Math.min(dropdownMaxHeight, spaceAbove - 20);
      maxHeight = Math.min(dropdownMaxHeight, spaceAbove - 20);
    } else {
      maxHeight = Math.min(dropdownMaxHeight, spaceBelow - 20);
    }
    
    return {
      top: top,
      left: rect.left,
      width: rect.width,
      maxHeight: maxHeight
    };
  }, []);

  const handleToggleDropdown = (key, e) => {
    if (e) {
      e.stopPropagation();
    }
    const isCurrentlyOpen = openDropdowns[key];
    if (!isCurrentlyOpen) {
      setSearchQueries(prev => ({ ...prev, [key]: '' }));
      // Calculate position before opening - استخدام requestAnimationFrame لضمان تحديث DOM
      requestAnimationFrame(() => {
        const position = calculateDropdownPosition(key);
        if (position) {
          setDropdownPositions(prev => ({ ...prev, [key]: position }));
        }
        setOpenDropdowns(prev => ({ ...prev, [key]: true }));
      });
    } else {
      setOpenDropdowns(prev => ({ ...prev, [key]: false }));
    }
  };

  // إغلاق dropdown عند النقر خارجه وتحديث الموضع عند التمرير
  useEffect(() => {
    let scrollTimeout;
    let resizeTimeout;

    const handleClickOutside = (event) => {
      if (!event.target.closest('.account-dropdown-container') && 
          !event.target.closest('.account-dropdown-list')) {
        setOpenDropdowns({});
      }
    };

    const updateDropdownPositions = () => {
      // تحديث موضع القوائم المفتوحة فقط - استخدام requestAnimationFrame
      requestAnimationFrame(() => {
        setOpenDropdowns(prevOpen => {
          Object.keys(prevOpen).forEach(key => {
            if (prevOpen[key]) {
              const position = calculateDropdownPosition(key);
              if (position) {
                setDropdownPositions(prev => {
                  const current = prev[key];
                  // تحديث فقط إذا تغير الموضع بشكل كبير (أكثر من 5px)
                  if (!current || 
                      Math.abs(current.top - position.top) > 5 || 
                      Math.abs(current.left - position.left) > 5) {
                    return { ...prev, [key]: position };
                  }
                  return prev;
                });
              }
            }
          });
          return prevOpen;
        });
      });
    };

    const handleScroll = () => {
      // استخدام debounce لتقليل التحديثات
      clearTimeout(scrollTimeout);
      scrollTimeout = setTimeout(() => {
        updateDropdownPositions();
      }, 50);
    };

    const handleResize = () => {
      // استخدام debounce لتقليل التحديثات
      clearTimeout(resizeTimeout);
      resizeTimeout = setTimeout(() => {
        updateDropdownPositions();
      }, 100);
    };

    document.addEventListener('mousedown', handleClickOutside);
    window.addEventListener('scroll', handleScroll, true);
    window.addEventListener('resize', handleResize);
    
    return () => {
      clearTimeout(scrollTimeout);
      clearTimeout(resizeTimeout);
      document.removeEventListener('mousedown', handleClickOutside);
      window.removeEventListener('scroll', handleScroll, true);
      window.removeEventListener('resize', handleResize);
    };
  }, [calculateDropdownPosition]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // جمع فقط الـ links التي تم تغييرها أو اختيارها
    const links = [];
    
    if (accountLinks?.data) {
      accountLinks.data.forEach(link => {
        // فقط الحسابات الموجودة في formData (تم تغييرها أو اختيارها من المستخدم)
        if (formData[link.key]) {
          const newAccountId = Number(formData[link.key]);
          const originalAccountId = link.linked_account?.id;
          
          // إرسال فقط إذا كان الحساب مختلف عن الأصلي أو لم يكن مربوط من قبل
          if (!originalAccountId || newAccountId !== originalAccountId) {
            links.push({
              key: link.key,
              account_id: newAccountId
            });
          }
        }
      });
    }

    if (links.length === 0) {
      toast.error(t('accountLinks.selectAtLeastOne'), { rtl: isRTL });
      return;
    }

    try {
      for (const link of links) {
        await dispatch(updateAccountLink(link)).unwrap();
      }
      toast.success(t('accountLinks.linkSuccess'), { rtl: isRTL });
      dispatch(getAccountLinks());
    } catch (error) {
      toast.error(error || t('accountLinks.linkError'), { rtl: isRTL });
    }
  };

  const getLabel = (link) => {
    return isRTL ? link.label_ar : link.label_en;
  };

  const getAccountName = (account) => {
    return isRTL ? (account.name_ar || account.name_en) : (account.name_en || account.name_ar);
  };

  if (isLoading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ height: '400px' }}>
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  // التحقق من أن كل الحسابات مربوطة ولا توجد تغييرات
  const allLinked = (() => {
    if (!accountLinks?.data || accountLinks.data.length === 0) return false;
    
    // كل الحسابات يجب أن تكون مربوطة
    const allAccountsLinked = accountLinks.data.every(link => !!link.linked_account);
    
    // لا توجد تغييرات في formData
    const noChanges = Object.keys(formData).length === 0;
    
    return allAccountsLinked && noChanges;
  })();

  return (
    <div className="content-wrapper" style={{ direction: dir }}>
      <style>
        {`
          .account-dropdown-container div[style*="overflowY: auto"]::-webkit-scrollbar {
            width: 8px;
          }
          .account-dropdown-container div[style*="overflowY: auto"]::-webkit-scrollbar-track {
            background: #1b3350;
            border-radius: 4px;
          }
          .account-dropdown-container div[style*="overflowY: auto"]::-webkit-scrollbar-thumb {
            background: #AC2000;
            border-radius: 4px;
          }
          .account-dropdown-container div[style*="overflowY: auto"]::-webkit-scrollbar-thumb:hover {
            background: #d42800;
          }
        `}
      </style>
      <div className="container-fluid">
        <div style={{ marginBottom: '30px' }}>
          <h2 style={{ color: 'white', fontFamily: 'Times New Roman, Times, serif, Montserrat-Arabic, sans-serif', fontWeight: 'bold', marginBottom: '20px', textAlign }}>
            {t('accountLinks.title')}
          </h2>
        </div>

        <form onSubmit={handleSubmit}>
          <div style={{
            backgroundColor: '#1a1f2e',
            borderRadius: '12px',
            padding: '30px',
            border: '1px solid #333',
            overflow: 'visible',
            position: 'relative'
          }}>
            {accountLinks?.data?.map((link, index) => {
              const linkedAccount = link.linked_account;
              // الحساب المختار من المستخدم أو الحساب المرتبط الأصلي
              const currentAccountId = formData[link.key] || (linkedAccount ? linkedAccount.id.toString() : null);
              const isDropdownOpen = openDropdowns[link.key];
              const disabledIds = getDisabledAccountIds(link.key);
              
              // استخدام filteredAccounts إذا كان موجوداً، وإلا استخدام unlinkedAccounts
              let accountsToShow = filteredAccounts[link.key] || [];
              
              // إذا كانت القائمة فارغة أو لم تكن مفتوحة، نعرض جميع الحسابات
              if (accountsToShow.length === 0 && unlinkedAccounts?.data) {
                accountsToShow = [...unlinkedAccounts.data];
              }
              
              // إضافة الحساب المرتبط الحالي إذا لم يكن موجوداً في القائمة
              if (linkedAccount && !accountsToShow.find(acc => acc.id === linkedAccount.id)) {
                accountsToShow = [linkedAccount, ...accountsToShow];
              }
              
              // تحديد الحساب المختار الحالي - البحث في جميع المصادر
              let selectedAccount = null;
              if (currentAccountId) {
                // البحث في accountsToShow أولاً
                selectedAccount = accountsToShow.find(acc => acc.id.toString() === currentAccountId);
                
                // إذا لم نجده، نبحث في linkedAccount
                if (!selectedAccount && linkedAccount && linkedAccount.id.toString() === currentAccountId) {
                  selectedAccount = linkedAccount;
                }
                
                // إذا لم نجده، نبحث في unlinkedAccounts
                if (!selectedAccount && unlinkedAccounts?.data) {
                  selectedAccount = unlinkedAccounts.data.find(acc => acc.id.toString() === currentAccountId);
                }
              }
              
              // عرض اسم الحساب المختار إذا كان موجوداً، وإلا عرض searchQuery
              const searchQuery = searchQueries[link.key] || '';
              const displayValue = isDropdownOpen 
                ? searchQuery 
                : (selectedAccount ? getAccountName(selectedAccount) : (searchQuery || ''));

              return (
                <motion.div
                  key={link.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                  style={{ marginBottom: '25px', overflow: 'visible', position: 'relative', zIndex: isDropdownOpen ? 1 : 'auto' }}
                >
                  <div style={{
                    backgroundColor: 'rgba(172, 32, 0, 0.1)',
                    border: '1px solid rgba(172, 32, 0, 0.3)',
                    borderRadius: '12px',
                    padding: '20px',
                    marginBottom: isDropdownOpen ? '15px' : '0',
                    overflow: 'visible'
                  }}>
                    <label style={{
                      display: 'block',
                      marginBottom: '12px',
                      color: 'white',
                      fontWeight: 'bold',
                      fontSize: '1rem',
                      fontFamily: 'Times New Roman, Times, serif, Montserrat-Arabic, sans-serif',
                      direction: dir,
                      textAlign
                    }}>
                      {getLabel(link)}
                    </label>

                    <div style={{ position: 'relative', marginBottom: isDropdownOpen ? '10px' : '0', overflow: 'visible' }} className="account-dropdown-container">
                      {linkedAccount && !isDropdownOpen && (
                        <div style={{
                          backgroundColor: '#AC2000',
                          border: '1px solid rgba(172, 32, 0, 0.5)',
                          borderRadius: '8px',
                          padding: '15px',
                          marginBottom: '10px'
                        }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                            <FaCheckCircle style={{ color: 'white', fontSize: '1.2rem' }} />
                            <div style={{ flex: 1 }}>
                              <div style={{ color: 'white', fontWeight: '600', marginBottom: '5px', direction: dir, textAlign }}>
                                {getLabel(link)}
                              </div>
                              <div style={{ color: 'white', fontSize: '0.9rem', opacity: 0.9, direction: dir, textAlign }}>
                                {t('accountLinks.code')}: {linkedAccount.code} | {t('accountLinks.type')}: {linkedAccount.type}
                              </div>
                            </div>
                          </div>
                        </div>
                      )}
                      
                      <div
                        style={{
                          position: 'relative',
                          cursor: 'pointer'
                        }}
                        onClick={(e) => handleToggleDropdown(link.key, e)}
                      >
                        <input
                          ref={(el) => inputRefs.current[link.key] = el}
                          type="text"
                          placeholder={currentAccountId ? t('accountLinks.changeAccount') : t('accountLinks.selectAccount')}
                          value={displayValue}
                          onChange={(e) => {
                            e.stopPropagation();
                            handleSearch(link.key, e.target.value);
                            if (!isDropdownOpen) {
                              requestAnimationFrame(() => {
                                const position = calculateDropdownPosition(link.key);
                                if (position) {
                                  setDropdownPositions(prev => ({ ...prev, [link.key]: position }));
                                }
                                setOpenDropdowns(prev => ({ ...prev, [link.key]: true }));
                              });
                            } else {
                              setOpenDropdowns(prev => ({ ...prev, [link.key]: true }));
                            }
                          }}
                          onFocus={(e) => {
                            e.stopPropagation();
                            if (!isDropdownOpen) {
                              requestAnimationFrame(() => {
                                const position = calculateDropdownPosition(link.key);
                                if (position) {
                                  setDropdownPositions(prev => ({ ...prev, [link.key]: position }));
                                }
                                setOpenDropdowns(prev => ({ ...prev, [link.key]: true }));
                              });
                            }
                          }}
                          onClick={(e) => {
                            e.stopPropagation();
                            if (!isDropdownOpen) {
                              requestAnimationFrame(() => {
                                const position = calculateDropdownPosition(link.key);
                                if (position) {
                                  setDropdownPositions(prev => ({ ...prev, [link.key]: position }));
                                }
                                setOpenDropdowns(prev => ({ ...prev, [link.key]: true }));
                              });
                            }
                          }}
                          readOnly={false}
                          style={{
                            width: '100%',
                            padding: '12px 15px',
                            backgroundColor: '#1a1f2e',
                            border: '1px solid #333',
                            borderRadius: '8px',
                            color: 'white',
                            fontSize: '14px',
                            cursor: isDropdownOpen || !currentAccountId ? 'text' : 'pointer',
                            direction: dir,
                            textAlign
                          }}
                        />
                      </div>

                      {isDropdownOpen && dropdownPositions[link.key] && ReactDOM.createPortal(
                        <div 
                          className="account-dropdown-list"
                          style={{
                            position: 'fixed',
                            top: `${dropdownPositions[link.key].top}px`,
                            left: `${dropdownPositions[link.key].left}px`,
                            width: `${dropdownPositions[link.key].width}px`,
                            backgroundColor: '#1b3350',
                            border: '1px solid #AC2000',
                            borderRadius: '8px',
                            maxHeight: `${dropdownPositions[link.key].maxHeight || 300}px`,
                            overflowY: 'auto',
                            overflowX: 'hidden',
                            zIndex: 10000,
                            boxShadow: '0 4px 12px rgba(0,0,0,0.5)',
                            paddingBottom: '16px',
                            paddingTop: '4px',
                            WebkitOverflowScrolling: 'touch',
                            scrollbarWidth: 'thin',
                            scrollbarColor: '#AC2000 #1b3350'
                          }}
                          onClick={(e) => e.stopPropagation()}
                        >
                          {accountsToShow.length > 0 ? (
                            accountsToShow.map((account, accountIndex) => {
                              const isDisabled = disabledIds.includes(account.id);
                              const isSelected = account.id.toString() === currentAccountId;
                              const isLastItem = accountIndex === accountsToShow.length - 1;
                              return (
                                <div
                                  key={account.id}
                                  onClick={() => !isDisabled && handleAccountSelect(link.key, account.id)}
                                  style={{
                                    padding: '12px 15px',
                                    paddingBottom: isLastItem ? '16px' : '12px',
                                    cursor: isDisabled ? 'not-allowed' : 'pointer',
                                    backgroundColor: isSelected ? '#AC2000' : (isDisabled ? 'rgba(107, 114, 128, 0.2)' : 'transparent'),
                                    color: isSelected ? 'white' : (isDisabled ? '#6b7280' : 'white'),
                                    opacity: isDisabled ? 0.5 : 1,
                                    borderBottom: isLastItem ? 'none' : '1px solid rgba(255,255,255,0.1)',
                                    minHeight: '60px',
                                    display: 'flex',
                                    flexDirection: 'column',
                                    justifyContent: 'center'
                                  }}
                                  onMouseEnter={(e) => {
                                    if (!isDisabled) {
                                      if (!isSelected) {
                                        e.target.style.backgroundColor = 'rgba(172, 32, 0, 0.2)';
                                      }
                                    }
                                  }}
                                  onMouseLeave={(e) => {
                                    if (!isDisabled) {
                                      e.target.style.backgroundColor = isSelected ? '#AC2000' : 'transparent';
                                    }
                                  }}
                                >
                                  <div style={{ fontWeight: '500', display: 'flex', alignItems: 'center', gap: '8px', color: isSelected ? 'white' : 'inherit', direction: isRTL ? 'rtl' : 'ltr', textAlign: isRTL ? 'right' : 'left' }}>
                                    {isSelected && <FaCheckCircle style={{ color: 'white', fontSize: '0.9rem' }} />}
                                    {getAccountName(account)}
                                  </div>
                                  <div style={{ fontSize: '0.85rem', color: isSelected ? 'white' : '#6b7280', marginTop: '4px', opacity: isSelected ? 0.9 : 1, direction: dir, textAlign }}>
                                    {t('accountLinks.code')}: {account.code} | {t('accountLinks.type')}: {account.type}
                                  </div>
                                </div>
                              );
                            })
                          ) : (
                            <div style={{ padding: '15px', color: '#6b7280', textAlign }}>
                              {t('accountLinks.noAccountsAvailable')}
                            </div>
                          )}
                        </div>,
                        document.body
                      )}
                    </div>

                  </div>
                </motion.div>
              );
            })}

            <div style={{ marginTop: '30px', textAlign: 'center' }}>
              <button
                type="submit"
                disabled={allLinked}
                style={{
                  backgroundColor: allLinked ? '#6b7280' : '#AC2000',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  padding: '12px 30px',
                  fontSize: '1rem',
                  fontWeight: 'bold',
                  cursor: allLinked ? 'not-allowed' : 'pointer',
                  transition: 'all 0.3s ease',
                  fontFamily: 'Times New Roman, Times, serif, Montserrat-Arabic, sans-serif',
                  opacity: allLinked ? 0.6 : 1
                }}
                onMouseEnter={(e) => {
                  if (!allLinked) {
                    e.target.style.backgroundColor = '#08a04a';
                    e.target.style.transform = 'translateY(-2px)';
                  }
                }}
                onMouseLeave={(e) => {
                  if (!allLinked) {
                    e.target.style.backgroundColor = '#AC2000';
                    e.target.style.transform = 'translateY(0)';
                  }
                }}
              >
                <FaLink style={{ [isRTL ? 'marginRight' : 'marginLeft']: '8px', display: 'inline-block' }} />
                {t('accountLinks.saveLink')}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AccountLinks;

